// =============================================================================
// PPIBanco — o banco de questões (carga da planilha + navegação)
// =============================================================================
// É aqui que entram as 1.000+ questões da Patrícia. A carga é IDEMPOTENTE:
// re-subir a planilha não duplica — questão que já existe e ainda NÃO caiu em
// prova é ATUALIZADA (é a correção dela); a que já caiu é PRESERVADA (mexer nela
// corromperia o ranking histórico, porque a prova guarda o gabarito congelado).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Download, Search, CheckCircle2, XCircle, AlertTriangle, FileSpreadsheet, Trash2, Lock } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';
import { baixarModeloQuestoes } from './ppiModeloQuestoes';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const jsonHeaders = () => ({ 'Content-Type': 'application/json', ...authHeaders() });

// Importar a planilha é SÓ para Admin/Diretoria (2026-07-15). A trava real é no
// backend (a rota exige OPERA); aqui só escondemos o botão para os demais.
const podeImportarPlanilha = () => {
    try {
        return ['Admin', 'Diretoria'].includes(perfilNoModulo((JSON.parse(localStorage.getItem('user') || '{}')).perfil, 'treinamento'));
    } catch (_) {
        return false;
    }
};

const TOM_NIVEL = { 'Fácil': 'aprovado', 'Médio': 'emProcesso', 'Difícil': 'reprovado' };

function PPIBanco() {
    const [cargos, setCargos] = useState([]);
    const [idCargo, setIdCargo] = useState('');
    const [areas, setAreas] = useState([]);
    const [idArea, setIdArea] = useState('');
    const [busca, setBusca] = useState('');
    const [dados, setDados] = useState(null);
    const [pagina, setPagina] = useState(1);
    const [erro, setErro] = useState('');
    const [resultado, setResultado] = useState(null);   // resposta da importação
    const [enviando, setEnviando] = useState(false);
    const inputArquivo = useRef(null);

    const carregarCargos = useCallback(async () => {
        try {
            const r = await fetch('/api/treinamento/ppi/prova/cargos', { headers: authHeaders() });
            if (r.ok) setCargos(await r.json());
        } catch (_) { /* */ }
    }, []);

    const carregarAreas = useCallback(async () => {
        if (!idCargo) { setAreas([]); return; }
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/areas?cargo=${idCargo}`, { headers: authHeaders() });
            if (r.ok) setAreas(await r.json());
        } catch (_) { /* */ }
    }, [idCargo]);

    const carregarQuestoes = useCallback(async () => {
        if (!idCargo) { setDados(null); return; }
        try {
            const p = new URLSearchParams();
            p.set('cargo', idCargo);
            if (idArea) p.set('area', idArea);
            if (busca) p.set('busca', busca);
            p.set('pagina', pagina);
            const r = await fetch(`/api/treinamento/ppi/prova/questoes?${p}`, { headers: authHeaders() });
            if (r.ok) setDados(await r.json());
        } catch (e) { setErro(e.message); }
    }, [idCargo, idArea, busca, pagina]);

    useEffect(() => { carregarCargos(); }, [carregarCargos]);
    useEffect(() => { carregarAreas(); }, [carregarAreas]);
    useEffect(() => { carregarQuestoes(); }, [carregarQuestoes]);

    const importar = async (e) => {
        const arquivo = e.target.files?.[0];
        if (!arquivo) return;
        if (!idCargo) { setErro('Escolha o cargo do PPI antes de importar.'); return; }
        setEnviando(true); setErro(''); setResultado(null);
        try {
            const fd = new FormData();
            fd.append('arquivo', arquivo);
            fd.append('idCargo', idCargo);
            const r = await fetch('/api/treinamento/ppi/prova/questoes/importar', {
                method: 'POST', headers: authHeaders(), body: fd,
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Erro ao importar.');
            setResultado(d);
            carregarAreas(); carregarQuestoes();
        } catch (err) { setErro(err.message); }
        finally {
            setEnviando(false);
            if (inputArquivo.current) inputArquivo.current.value = '';
        }
    };

    const novoCargo = async () => {
        const nome = window.prompt('Nome da trilha (ex.: "Atendente → Treinador"):');
        if (!nome?.trim()) return;
        try {
            const r = await fetch('/api/treinamento/ppi/prova/cargos', {
                method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ nome }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            await carregarCargos();
            setIdCargo(String(d.id));
        } catch (e) { setErro(e.message); }
    };

    const alternarAtiva = async (id, ativa) => {
        try {
            await fetch(`/api/treinamento/ppi/prova/questoes/${id}/ativa`, {
                method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ ativa }),
            });
            carregarQuestoes(); carregarAreas();
        } catch (e) { setErro(e.message); }
    };

    // EXCLUIR (questão errada). Só some do banco se NUNCA caiu em prova.
    // Se já caiu, o backend recusa com 409 e explica: apagá-la deixaria o gabarito
    // congelado daquelas provas apontando para alternativas que não existem mais, e
    // o ranking histórico deixaria de fechar. O caminho ali é Desativar (tira do
    // sorteio, histórico intacto). A tela nem oferece o botão nesse caso — mas a
    // trava de verdade é a do servidor, não esta.
    const excluir = async (q) => {
        if (!window.confirm(`Apagar esta questão do banco?\n\n"${String(q.TEXTO).slice(0, 120)}"\n\nEla some com as alternativas. Não dá para desfazer.`)) return;
        setErro('');
        try {
            const r = await fetch(`/api/treinamento/ppi/prova/questoes/${q.ID}`, {
                method: 'DELETE', headers: jsonHeaders(),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message);
            carregarQuestoes(); carregarAreas();
        } catch (e) { setErro(e.message); }
    };

    const totalPaginas = dados ? Math.ceil(dados.total / dados.porPagina) : 1;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
                <div>
                    <h3 style={estilos.tituloSecao}>Banco de questões</h3>
                    <p style={estilos.subtituloSecao}>As provas são montadas sorteando daqui.</p>
                </div>
                {/* O MODELO fica fora da trava de importação de propósito: quem MONTA a
                    planilha (Treinamento) não é quem a SOBE (Admin/Diretoria). Baixar o
                    modelo não escreve nada — é só o cabeçalho que a carga entende. */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Botao variant="secundario" icone={Download} onClick={baixarModeloQuestoes}>
                        Baixar modelo
                    </Botao>
                    {podeImportarPlanilha() && (
                        <>
                            <input ref={inputArquivo} type="file" accept=".csv,.xlsx,.xls" onChange={importar} style={{ display: 'none' }} />
                            <Botao icone={Upload} onClick={() => inputArquivo.current?.click()} disabled={enviando || !idCargo}>
                                {enviando ? 'Importando…' : 'Importar planilha'}
                            </Botao>
                        </>
                    )}
                </div>
            </div>

            {/* Formato esperado */}
            <div style={{ backgroundColor: CORES.bgSuave, border: `1px solid ${CORES.borda}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '18px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <FileSpreadsheet size={18} color={CORES.textoFraco} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '12px', color: CORES.textoSec, lineHeight: 1.6 }}>
                    <strong>Formato (.csv ou .xlsx):</strong> <code>id · area · subarea · nivel · tipo · enunciado · alternativa_a…d · resposta_correta · explicacao · pontuacao · area_critica · status · codigo_guia</code>
                    <br />
                    <strong>Re-subir não duplica:</strong> questão que ainda não caiu em prova é <strong>atualizada</strong>; a que já caiu é <strong>preservada</strong> (mexer nela mudaria o ranking histórico).
                    <br />
                    <strong>Não sabe o formato?</strong> Use o <strong>Baixar modelo</strong> acima — ele vem com o cabeçalho exato, uma aba de exemplo e o que cada coluna significa. Preencha a aba <code>Questoes</code> (é a única que a carga lê).
                </div>
            </div>

            {/* CARGO do PPI — separa os bancos. Sem ele, nada aparece. */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '18px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '260px' }}>
                    <label style={estilos.rotuloFiltro}>Cargo do PPI (trilha)</label>
                    <select style={estilos.input} value={idCargo} onChange={(e) => { setIdCargo(e.target.value); setIdArea(''); setPagina(1); }}>
                        <option value="">Selecione a trilha…</option>
                        {cargos.map((c) => (
                            <option key={c.ID} value={c.ID}>{c.NOME} ({c.QTD_QUESTOES} questões)</option>
                        ))}
                    </select>
                </div>
                <Botao variant="secundario" onClick={novoCargo}>Novo cargo</Botao>
            </div>

            {!idCargo && (
                <p style={{ color: CORES.textoFraco, fontSize: '13px' }}>
                    Escolha a trilha acima. Cada cargo tem o <strong>seu próprio banco</strong> — a prova de Treinador só sorteia
                    questões de Treinador.
                </p>
            )}

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}

            {/* Resultado da importação */}
            {resultado && (
                <div style={{ backgroundColor: '#ecfdf5', borderRadius: '10px', padding: '14px', marginBottom: '18px' }}>
                    <p style={{ margin: 0, fontWeight: 700, color: '#047857' }}>
                        {resultado.lidas} linha(s) lida(s) · <strong>{resultado.inseridas}</strong> nova(s) ·{' '}
                        <strong>{resultado.atualizadas}</strong> atualizada(s) · {resultado.preservadas} preservada(s)
                    </p>
                    {resultado.totalErros > 0 && (
                        <div style={{ marginTop: '10px' }}>
                            <p style={{ margin: '0 0 6px 0', color: '#be123c', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <AlertTriangle size={14} /> {resultado.totalErros} linha(s) com problema (não entraram):
                            </p>
                            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#be123c' }}>
                                {resultado.erros.map((e, i) => (
                                    <li key={i}>linha {e.linha}{e.codigo ? ` (${e.codigo})` : ''}: {e.motivo}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* Estoque por área e nível — é o que a montagem da prova consome */}
            <div style={{ ...estilos.tabelaWrap, marginBottom: '20px' }}>
                <table style={estilos.tabela}>
                    <thead><tr>
                        <th style={estilos.th}>Área</th>
                        <th style={estilos.th}>Ativas</th>
                        <th style={estilos.th}>Inéditas (nunca caíram)</th>
                        <th style={estilos.th}>Por nível</th>
                    </tr></thead>
                    <tbody>
                        {areas.map((a) => (
                            <tr key={a.id}>
                                <td style={estilos.td}><strong>{a.nome}</strong></td>
                                <td style={{ ...estilos.td, fontWeight: 800 }}>{a.ativas}</td>
                                <td style={estilos.td}>{a.ineditas}</td>
                                <td style={estilos.td}>
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        {Object.entries(a.niveis).map(([nivel, n]) => (
                                            <BadgeStatus key={nivel} tom={TOM_NIVEL[nivel] || 'neutro'}>
                                                {nivel}: {n.ativas}
                                            </BadgeStatus>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!areas.length && <tr><td style={estilos.td} colSpan={4}>Banco vazio — importe a planilha.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Navegação nas questões */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '14px' }}>
                <div>
                    <label style={estilos.rotuloFiltro}>Área</label>
                    <select style={estilos.input} value={idArea} onChange={(e) => { setIdArea(e.target.value); setPagina(1); }}>
                        <option value="">Todas</option>
                        {areas.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Buscar no enunciado</label>
                    <div style={{ position: 'relative' }}>
                        <Search size={14} color={CORES.textoFraco} style={{ position: 'absolute', left: '10px', top: '12px' }} />
                        <input style={{ ...estilos.input, paddingLeft: '30px' }} value={busca}
                            onChange={(e) => { setBusca(e.target.value); setPagina(1); }} placeholder="palavra-chave…" />
                    </div>
                </div>
            </div>

            {dados && (
                <>
                    <div style={estilos.tabelaWrap}>
                        <table style={estilos.tabela}>
                            <thead><tr>
                                <th style={estilos.th}>Área</th>
                                <th style={estilos.th}>Nível</th>
                                <th style={estilos.th}>Enunciado</th>
                                <th style={estilos.th}>Já caiu</th>
                                <th style={{ ...estilos.th, textAlign: 'center' }}>No sorteio</th>
                                <th style={{ ...estilos.th, textAlign: 'center' }}>Excluir</th>
                            </tr></thead>
                            <tbody>
                                {dados.questoes.map((q) => {
                                    const jaCaiu = Number(q.VEZES_USADA) > 0;
                                    return (
                                        <tr key={q.ID} style={{ opacity: q.ATIVA ? 1 : 0.5 }}>
                                            <td style={estilos.td}>{q.AREA}</td>
                                            <td style={estilos.td}>
                                                {q.NIVEL
                                                    ? <BadgeStatus tom={TOM_NIVEL[q.NIVEL] || 'neutro'}>{q.NIVEL}</BadgeStatus>
                                                    : <span style={{ color: CORES.textoFraco, fontSize: '12px' }}>—</span>}
                                            </td>
                                            <td style={{ ...estilos.td, maxWidth: '460px' }}>{q.TEXTO}</td>
                                            <td style={estilos.td}>
                                                {jaCaiu
                                                    ? <BadgeStatus tom="emProcesso">{q.VEZES_USADA}x</BadgeStatus>
                                                    : <span style={{ color: CORES.textoFraco, fontSize: '12px' }}>inédita</span>}
                                            </td>
                                            <td style={{ ...estilos.td, textAlign: 'center' }}>
                                                <button
                                                    onClick={() => alternarAtiva(q.ID, !q.ATIVA)}
                                                    title={q.ATIVA ? 'Tirar do sorteio' : 'Voltar para o sorteio'}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: q.ATIVA ? '#059669' : CORES.textoFraco }}>
                                                    {q.ATIVA ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                                                </button>
                                            </td>
                                            <td style={{ ...estilos.td, textAlign: 'center' }}>
                                                {/* Questão que JÁ CAIU em prova não pode ser apagada: o gabarito
                                                    congelado daquelas provas aponta para as alternativas dela, e
                                                    apagá-la corromperia o ranking de quem já respondeu. O caminho
                                                    é Desativar (o ✓ ao lado) — sai do sorteio, histórico intacto. */}
                                                {jaCaiu ? (
                                                    <span
                                                        title={`Já caiu em ${q.VEZES_USADA} prova(s) — apagar corromperia o ranking histórico. Use "No sorteio" para desativá-la.`}
                                                        style={{ color: '#cbd5e1', cursor: 'help' }}>
                                                        <Lock size={16} />
                                                    </span>
                                                ) : (
                                                    <button onClick={() => excluir(q)} title="Apagar do banco (questão inédita)"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e11d48' }}>
                                                        <Trash2 size={17} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!dados.questoes.length && <tr><td style={estilos.td} colSpan={6}>Nenhuma questão encontrada.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <p style={{ fontSize: '12px', color: CORES.textoFraco, marginTop: '8px' }}>
                        Só dá para <strong>excluir</strong> questão que nunca caiu em prova. A que já caiu fica travada (🔒):
                        apagá-la deixaria o gabarito daquelas provas órfão e o <strong>ranking histórico deixaria de fechar</strong>.
                        Para tirá-la de circulação, <strong>desative</strong> — ela some do sorteio e o histórico continua intacto.
                    </p>

                    {totalPaginas > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '14px' }}>
                            <Botao variant="secundario" onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={pagina === 1}>Anterior</Botao>
                            <span style={{ fontSize: '13px', color: CORES.textoFraco }}>
                                {pagina} / {totalPaginas} · {dados.total} questões
                            </span>
                            <Botao variant="secundario" onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))} disabled={pagina >= totalPaginas}>Próxima</Botao>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default PPIBanco;
